/**
 * Opener layout contract, ported from boxed-frontend
 * `components/box/opener-layout/openerContract.ts`. This is the seam opener
 * styles plug into: a box's opener style names a kind (`box.opener.kind`),
 * and `openers/registry.ts` maps each kind to a descriptor. A new animation
 * (e.g. a TCG pack-ripper) ships a descriptor there and a kind in
 * api-contract's `OPENER_KINDS`, never a new shell conditional.
 *
 * Theming, for every opener: draw only from the `--los-*` variables that
 * `resolveTheme` sets on the opener root (and `theme` from
 * `useLootBoxContext()` where a value is needed, e.g. SVG paints). That makes
 * an opener follow its host's theme by default (the storefront theme, or a
 * Shopify shop's opener theme in the overlay) and honor its style's overrides
 * token by token (`mergeLootTheme`). Never hardcode a color, radius or font.
 */

import type { RandomGenerator } from "pure-rand/types/RandomGenerator";
import type { ComponentType, ReactNode } from "react";
import type { OpenerTokenFallback } from "../theme/theme";
import type { OpenerClaimStatus, OpenerMachinePhase } from "./machine";
import type { BoxDetail, ClaimedItem, OpenEntry } from "./types";

/** The box a mounted opener works with (boxed-frontend's `Box`, renamed). */
export type LootBoxData = BoxDetail;

/**
 * The button a settled reveal shows in place of "Open again", for a host whose box opens only
 * once (a box bought as a product: another open is another purchase, not another click).
 */
export interface OpenerSettledAction {
  label: string;
  onClick: () => void;
}

/**
 * What `renderIdle` gets: everything the opener's own idle view uses, so a host can draw the whole
 * idle state (art, name, price, quantity, Open) in its own layout while the opener keeps the
 * open itself, the reveal and the results.
 */
export interface OpenerIdleProps {
  boxData: LootBoxData;
  /** Opens at the chosen quantity: a demo open signed out, a paid one signed in. */
  open: () => void;
  quantity: number;
  setQuantity: (quantity: number) => void;
  /** The quantities offered, in order; empty when there is no choice (prepaid, or a cap of 1). */
  quantities: readonly number[];
  /** The fast-reveal switch, when the host asked for one (`quickReveal="toggle"`). */
  quickReveal?: { on: boolean; set: (on: boolean) => void };
  /** Signed out: the open is a demo, nothing is charged or kept. */
  demo: boolean;
  /** Signed in: open a demo instead, for nothing, keeping nothing. Absent signed out or prepaid. */
  demoOpen?: () => void;
  /** An open is on its way: Open and the quantity are disabled. */
  requesting: boolean;
  /** Why the last open was refused; null when it wasn't. */
  errorMessage: string | null;
  /** The host took payment before the open: no price, no quantity. */
  prepaid: boolean;
  /**
   * The shopper's mute, for the host's idle to draw beside its open; absent when there is none to
   * offer (no `onSoundMutedChange`, or the store has no sound). The opener draws it in every other
   * phase.
   */
  sound?: OpenerIdleSound;
}

/**
 * What `renderResults` gets: the revealed entries and the settlement step, so a host can draw the
 * results in its own layout. Revealed items are not owned until claimed, so an `unclaimed` reveal
 * offers Claim at `claimCostCents` (and Sell back, when `sellBack` is set) before Open again.
 */
export interface OpenerResultsProps {
  entries: OpenEntry[];
  /** "none" is a reveal that needs no claim (claimed already, or settled before the open). */
  claimStatus: OpenerClaimStatus;
  /** The price Claim pays; null when there is none to show (prepaid, or nothing to claim). */
  claimCostCents: number | null;
  /** Purchases the revealed items. */
  claim: () => void;
  /** Purchases the revealed items and sells them to the house for their value, in one call. */
  sellBack?: () => void;
  /** Saves the shopper's auto-claim setting for later opens; absent when it cannot be saved. */
  setAutoClaim?: (autoClaim: boolean) => void;
  claimErrorMessage: string | null;
  sellErrorMessage: string | null;
  /** The units the reveal became once claimed, in entry order; empty before that. */
  claimedItems: ClaimedItem[];
  /** Back to the idle state, for the next open. */
  openAgain: () => void;
  /** Replaces Open again once the reveal settles (a box that opens only once). */
  settledAction?: OpenerSettledAction;
  /** The open was a demo (signed out, or a signed-in shopper's Demo open): nothing was charged or kept. */
  demo: boolean;
}

/**
 * What `renderControls` gets, by phase: before an open the idle props (quantity, Open), during it
 * Skip, after it the results' settlement step. The host draws them under the stage for every
 * opener kind, in place of the opener's own controls.
 */
export type OpenerControlsProps =
  | { phase: "pre"; idle: OpenerIdleProps }
  | { phase: "during"; skip: (() => void) | null }
  | { phase: "post"; results: OpenerResultsProps };

/** A mute toggle's state: one label whatever the state, pressed meaning muted. */
export interface OpenerIdleSound {
  label: string;
  muted: boolean;
  setMuted: (muted: boolean) => void;
}

/**
 * Which chrome each surface shows for a given machine phase — a 3-value
 * summary of the 4-phase machine. `idle` and `requesting` display the same
 * `pre` chrome but still behave differently (requesting disables actions).
 */
export type OpenerPhase = "pre" | "during" | "post";

/** Single place the machine-phase → `OpenerPhase` mapping is defined. */
export function getOpenerPhase(phase: OpenerMachinePhase): OpenerPhase {
  switch (phase) {
    case "revealing":
      return "during";
    case "results":
      return "post";
    default:
      return "pre";
  }
}

/**
 * Everything the active opener publishes for the shell-owned during-phase
 * action row. `custom` is the opener-specific controls payload, consumed only
 * by the same opener's descriptor `ActionRow` (opaque to the shell);
 * null = no extra controls beyond the shared Skip.
 */
export interface OpenerControlsState<TCustom = unknown> {
  /** Show + enable the shared Skip button right now. */
  canSkip: boolean;
  /** Synchronous pre-skip work (e.g. analytics with live progress), run by
   * the shell immediately before the skip transition. */
  onBeforeSkip?: () => void;
  custom: TCustom;
}

/** The reveal payload an opener animates (from the machine, not a store). */
export interface OpenerRevealProps {
  entries: OpenEntry[];
  quantity: number;
  isQuickReveal: boolean;
  /** Deterministic filler-item generator (tests); defaults to Math.random. */
  prandGenerator?: RandomGenerator;
}

/**
 * Props every shell-native opener implements.
 */
export interface OpenerComponentProps<TCustom = unknown> {
  boxData: LootBoxData;
  reveal: OpenerRevealProps;
  /** The opener's single exit from the reveal. Natural completion ends
   * here; the shell applies the descriptor's `revealExit` timing. Openers
   * never advance the machine themselves. */
  onRevealComplete: () => void;
  /** Publish the controls state whenever it changes (from an effect). MUST
   * publish referentially-stable objects (useMemo + stable callbacks) — the
   * shell's setState bails on identity, so churny republishes stay free. */
  onControlsStateChange: (state: OpenerControlsState<TCustom>) => void;
  /**
   * Set only for a stage-owning opener (`stage: "all"`): the phase the shell is in. Before the
   * open `reveal.entries` is empty; the opener idles until the phase is "during".
   */
  phase?: OpenerPhase;
  /**
   * Set only for a stage-owning opener: the shell's controls for the current phase (Open and
   * quantity before, Skip during, Claim / Sell back / Open again after), for the opener to place
   * inside its scene. The shell keeps every action; the opener only lays them out.
   */
  controls?: ReactNode;
}

/** Props of a descriptor `ActionRow` — the during-phase controls row the
 * shell renders below the reveal. */
export interface OpenerActionRowProps<TCustom = unknown> {
  /** The latest opener-published controls state. */
  controls: OpenerControlsState<TCustom>;
  /** Shell exit-controller skip — reveal-exit timing stays shell-owned. */
  onSkip: () => void;
}

/**
 * Registry entry describing an opener to the shell, consumed instead of
 * per-opener conditionals.
 */
export interface OpenerDescriptor<TCustom = unknown> {
  Opener: ComponentType<OpenerComponentProps<TCustom>>;
  /** During-phase action row; omit for the shared skip-only row. */
  ActionRow?: ComponentType<OpenerActionRowProps<TCustom>>;
  /** Where "Open again" loops back to: 'initial' re-runs the opener's own
   * intro (pack pick), 'loop' idles in the idle preview. */
  loopTarget: "initial" | "loop";
  /**
   * "reveal" (the default): mounted for the reveal only, the shell drawing the idle preview and
   * the results. "all": one instance from idle through results, so the scene is continuous; the
   * shell passes `phase` and `controls`, and remounts it only for the next open.
   */
  stage?: "reveal" | "all";
  /** Whether the quantity selector offers bulk quantities. */
  supportsBulk: boolean;
  /**
   * The most items this kind animates in one reveal; an open of more reveals its most valuable
   * item alone, and the results show every one. Left out, it animates every item.
   */
  revealLimit?: number;
  /**
   * The most items one bulk open of this kind can reveal (`DEFAULT_BULK_LIMIT` when left out): a
   * larger step a host offers (`quantities`) is left off until the kind can show it.
   */
  bulkLimit?: number;
  /** Exit timing after `onRevealComplete()`; `lingerMs` 0 = exit
   * synchronously in the same tick. */
  revealExit: {
    lingerMs: { normal: number; quick: number };
  };
  /**
   * The kind's own theme variables and where each falls back to (`OPENER_KIND_TOKENS` in
   * api-contract lists the same). The opener reads them through `resolveKindTokens`.
   */
  tokens?: Readonly<Record<string, OpenerTokenFallback>>;
  /** Starts loading a lazily loaded opener, so the reveal does not wait on the download. */
  preload?: () => void;
}

/**
 * Type-erasing helper: lets a descriptor's `Opener` and `ActionRow` share a
 * concrete `TCustom` at the definition site while a registry stays
 * heterogeneous (`OpenerDescriptor<unknown>`).
 */
export function defineOpenerDescriptor<T>(descriptor: OpenerDescriptor<T>): OpenerDescriptor {
  return descriptor as OpenerDescriptor;
}
